import { getSafeNetworksStore } from '../storage/stores';
import { captureException } from '../helper/sentry';
import { fetchSafeNetworksSettings } from '../api/fetchSafeNetworksSettings';
import { APIError } from '../lib/errors/api/APIError';
import { updateSettingsAndActiveSafeNetwork } from './index';
import { now } from '../lib/dateTime';
import { delayPromise } from '../helper/delay';

const SETTINGS_UPDATE_FREQUENCY_MS = 24 * 60 * 60 * 1000; // 24h
const SETTINGS_UPDATE_DELAY_MS = 10 * 1000; // 10sec

const SETTINGS_RETRY_FREQUENCY_MS = 5 * 60 * 1000; // 5min

/**
 * Fetch and save Safe Networks rules.
 */
export const updateSafeNetworksSettings = async (
  onSafeNetworkFeatureDisabled: () => Promise<void>
) => {
  try {
    const settings = await fetchSafeNetworksSettings();
    // If we don't get an error from the fetch, we can assume that the feature is enabled.
    if (!getSafeNetworksStore().isFeatureEnabled()) {
      getSafeNetworksStore().setFeatureEnabled(true);
      console.log('Safe Networks feature enabled!');
    }
    // Save current Safe Network
    getSafeNetworksStore().setValidSafeNetworks(
      settings.networks.map(network => ({
        url: network.url,
        expectedStatusCode: network.expected_status_code,
        expectedContentMatch: network.expected_content_match,
      }))
    );
    console.log(`Valid Safe Networks updated!`);
    console.log(JSON.stringify(getSafeNetworksStore().getValidSafeNetworks()));
  } catch (error) {
    // When receiving a 412 from this endpoint, it means that the feature is off.
    if (typeof error === 'object' && (error as APIError).status === 412) {
      if (getSafeNetworksStore().isFeatureEnabled()) {
        await onSafeNetworkFeatureDisabled();
        console.log('Safe Networks feature disabled by 412 error!');
      }
    } else if (
      typeof error === 'object' &&
      (error as APIError).status === APIError.STATUS_NETWORK_ERROR
    ) {
      console.warn(error);
      getSafeNetworksStore().setNextUpdateTimestamp(
        now() + SETTINGS_RETRY_FREQUENCY_MS
      );
    } else {
      console.error(error);
      captureException(error as Error);
    }
  }
};

export const updateSafeNetworksSettingsIfNeeded = () => {
  const nextUpdate = getSafeNetworksStore().getNextUpdateTimestamp();
  if (now() >= nextUpdate) {
    getSafeNetworksStore().setNextUpdateTimestamp(
      now() + SETTINGS_UPDATE_FREQUENCY_MS
    );
    if (nextUpdate !== -1) {
      // Delay execution in case network is still preparing and skip it the first time
      return delayPromise(
        updateSettingsAndActiveSafeNetwork,
        SETTINGS_UPDATE_DELAY_MS
      );
    }
  }
};
